import { EnsureQueryDataOptions, QueryClient } from '@tanstack/react-query';
import { AnyFunction, isFunction } from '@mews/std';
import { getRepositoryQueryKey } from './getRepositoryQueryKey';

export const ensureRepositoryQueryDataFactory = <RepositoryRegistry>(
    queryClient: QueryClient,
    repositories: RepositoryRegistry
) =>
    async function ensureRepositoryQueryData<
        RepositoryName extends keyof RepositoryRegistry,
        MethodName extends keyof RepositoryRegistry[RepositoryName],
        Method extends RepositoryRegistry[RepositoryName][MethodName] extends AnyFunction
            ? RepositoryRegistry[RepositoryName][MethodName]
            : never,
        Data = Awaited<ReturnType<Method>>,
    >(
        repositoryName: RepositoryName,
        methodName: MethodName,
        params: Parameters<Method>[0],
        queryOptions?: Omit<EnsureQueryDataOptions<Awaited<ReturnType<Method>>, Error, Data>, 'queryKey' | 'queryFn'>
    ) {
        return queryClient.ensureQueryData({
            // eslint-disable-next-line @tanstack/query/exhaustive-deps
            queryKey: getRepositoryQueryKey<RepositoryRegistry, RepositoryName, MethodName, typeof params>(
                repositoryName,
                methodName,
                params
            ),

            queryFn: async ({ signal }): Promise<Awaited<ReturnType<Method>>> => {
                const method = repositories[repositoryName][methodName];
                return isFunction(method) ? method(params, { signal }) : await Promise.reject();
            },
            ...queryOptions,
        });
    };
